import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { envType } from '@atlassiansox/analytics-web-client';
import { initializeAnalyticsClient } from '@databases/analytics';
import { setActivationId } from '@databases/database-state';
import { DatabaseContentContext, setCloudId } from '@databases/database-state';
import { StatsigClient } from '@databases/feature-flags/client/statsig';
import { getFeatureFlagsInUse } from '@databases/feature-flags/manager';
import { featureFlagClientManager } from '@databases/feature-flags/manager';
import CommonApp from '@databases/frontend/App';
import AppContent from '@databases/frontend/AppContent';
import { appStore } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { setContext } from '@databases/utils/redux/context/actions';
import { loadDatabaseByReferenceId } from '@databases/utils/redux/databases/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import { checkAppMode, setAppMode } from '@databases/utils/utils';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { currentUserState } from '@canvas/canvas-state';
import { setIdentity } from '@canvas/connection/identity';
import { type CanvasIdentity, IdentityKind } from '@canvas/model/identity';
import * as Sentry from '@canvas/sentry-react';

import { changeTheme } from '../initialize-database-theme';

/**
 * Identity profile sent from CFE, it has a similar shape to {@link CanvasIdentity} but a
 * few differences:
 * * It currently sends the user's `displayName` but calls it `nickname`
 * * `type` is missing since it's application-specific context
 */
type IdentityFromBridgeData = Omit<CanvasIdentity, 'type' | 'displayName'>;

type AppProps = {
  config: {
    cloudId: string;
    spaceId: string;
    spaceKey: string;
    userId: string;
    contentId: string;
    activationId: string;
    contentReferenceId: string;
    spaceName: string;
    title: string;
    identity: IdentityFromBridgeData;
  };
};

const App = ({
  config: {
    cloudId,
    spaceId,
    spaceKey,
    userId,
    contentId,
    activationId,
    contentReferenceId: referenceId,
    spaceName,
    title,
    identity,
  },
}: AppProps) => {
  setAppMode(checkAppMode());
  const intl = useIntl();

  useEffect(() => {
    Sentry.getCurrentScope().setTag(
      'cloudId',
      process.env.ENVTYPE === envType.LOCAL || !process.env.ENVTYPE ? 'local' : cloudId
    );
    setCloudId(cloudId);
  }, [cloudId]);

  useEffect(() => {
    Sentry.setUser({ id: userId });
  }, [userId]);

  useEffect(() => {
    setActivationId(activationId);
  }, [activationId]);

  useEffect(() => {
    setIdentity({
      ...identity,
      // TODO - Profile information is passed in from CFE via a data attribute
      // We'll need to add `displayName` to the bridge data on the CFE so we can
      // remove this
      displayName: identity.nickname,
      type: IdentityKind.CanvasId,
    });
    currentUserState.status = 'succeeded';
    currentUserState.identity = {
      ...identity,
      type: IdentityKind.CanvasId,
    };
  }, [identity]);

  const [analyticsClientLoaded, setAnalyticsClientLoaded] = useState(false);
  useEffect(() => {
    const loadAnalyticsClient = async () => {
      await initializeAnalyticsClient(
        process.env.ENVTYPE === envType.LOCAL || !process.env.ENVTYPE
          ? undefined
          : cloudId,
        spaceId,
        userId,
        process.env.ENVTYPE ?? envType.LOCAL,
        process.env.SENTRY_RELEASE ?? 'development'
      );
      setAnalyticsClientLoaded(true);
    };
    loadAnalyticsClient();
  }, [cloudId, spaceId, userId]);

  const [databaseFlagsLoaded, setDatabaseFlagsLoaded] = useState(false);
  useEffect(() => {
    const loadFeatureFlags = async () => {
      setBooleanFeatureFlagResolver((flagKey) => {
        return [
          'platform.linking-platform.datasource.show-jlol-basic-filters',
          'platform.linking-platform.datasource.show-clol-basic-filters',
        ].includes(flagKey);
      });
      featureFlagClientManager.add(
        new StatsigClient(
          (process.env.BUILD_MODE as 'stg-east' | 'prod-east') ?? 'stg-east',
          cloudId,
          userId
        )
      );
      await featureFlagClientManager.initialize();

      const featureFlagsInUse = getFeatureFlagsInUse();
      if (featureFlagsInUse.length > 0) {
        Sentry.setContext('Feature Flags', {
          'In Use': featureFlagsInUse,
        });
      }
      setDatabaseFlagsLoaded(true);
    };
    loadFeatureFlags();
  }, [cloudId, userId]);

  useLayoutEffect(() => {
    // Focus iframe to enable keyboard shortcuts
    window.focus();

    const listenOnThemeChange = async () => {
      const bridge = await getBridgeAPI();
      if (bridge.onThemeChange) {
        bridge.onThemeChange((theme) => {
          changeTheme(theme);
        });
      }
    };

    listenOnThemeChange();
  }, []);

  const [storeInitialized, setStoreInitialized] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(
        setContext({
          space: {
            key: spaceKey,
          },
          userId,
        })
      );
      setStoreInitialized(true);
    });
  }, [spaceKey, userId]);

  const queryParams = useMemo(
    // eslint-disable-next-line no-restricted-syntax
    () => new URLSearchParams(window.parent.location.search),
    []
  );

  const contentContext = useMemo(
    () => ({
      contentId,
      referencing: queryParams.get('referencing') === 'true',
      activationId,
      cloudId,
      setContentId: () => {}, // used by referencing dialog, but not used in standalone (where confluence defines the contentId)
      spaceKey,
      savedViewId: queryParams.get('savedViewId'),
      unsavedViewId: queryParams.get('unsavedView'),
      entryId: queryParams.get('entryId'),
      fieldId: queryParams.get('fieldId'),
      referenceId,
      spaceId,
      parentContentId: contentId,
      spaceName,
      title,
    }),
    [
      contentId,
      activationId,
      cloudId,
      spaceKey,
      referenceId,
      spaceId,
      spaceName,
      title,
      queryParams,
    ]
  );

  useEffect(() => {
    if (!contentId || !referenceId || !spaceId || !spaceKey) {
      return;
    }

    GlobalPageLoadExperience.mark('database-load-start');
    appStore.dispatch(
      loadDatabaseByReferenceId(
        {
          contentId,
          referenceId,
          spaceId: Number(spaceId),
          spaceKey,
          intl,
          spaceName: spaceName ?? '',
          title: title ?? '',
        },
        { throttle: false }
      )
    );
  }, [contentId, intl, referenceId, spaceId, spaceKey, spaceName, title]);

  if (!analyticsClientLoaded || !databaseFlagsLoaded || !storeInitialized) {
    return null;
  }

  return (
    <DatabaseContentContext.Provider value={contentContext}>
      <CommonApp aaid={userId}>
        <AppContent />
      </CommonApp>
    </DatabaseContentContext.Provider>
  );
};

export default App;
